import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationRow,
} from "@/lib/notifications.functions";

export function NotificationBell() {
  const fetchList = useServerFn(listNotifications);
  const markOne = useServerFn(markNotificationRead);
  const markAll = useServerFn(markAllNotificationsRead);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const r = await fetchList();
      setItems(r.items);
      setUnread(r.unread);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    void load();
    // Realtime fan-out scoped to the current user. Falls back gracefully if
    // there is no session (channel never receives anything).
    void supabase.auth.getUser().then(({ data }) => {
      const uid = data.user?.id;
      if (cancelled || !uid) return;
      channel = supabase
        .channel(`notifications:${uid}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` },
          () => { void load(); },
        )
        .subscribe();
    });
    // Light polling as a safety net (e.g. tab woken from sleep before realtime reconnects).
    const id = setInterval(load, 60_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      if (channel) void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const onItemClick = async (n: NotificationRow) => {
    if (!n.read_at) {
      await markOne({ data: { id: n.id } });
      setItems((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)),
      );
      setUnread((u) => Math.max(0, u - 1));
    }
  };

  const onMarkAll = async () => {
    await markAll();
    setItems((prev) => prev.map((x) => ({ ...x, read_at: x.read_at ?? new Date().toISOString() })));
    setUnread(0);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative text-muted-foreground hover:text-accent transition-colors"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 bg-accent text-[10px] font-medium text-accent-foreground flex items-center justify-center rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed left-4 right-4 top-16 z-50 max-h-[calc(100vh-5rem)] overflow-y-auto bg-card border border-border shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-3 sm:w-[360px] sm:max-w-[calc(100vw-2rem)] sm:max-h-[480px]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="text-xs tracking-wide-luxury text-muted-foreground uppercase">
                Notifications
              </p>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button
                    onClick={onMarkAll}
                    className="text-[11px] tracking-wide-luxury text-accent hover:underline"
                  >
                    Mark all read
                  </button>
                )}
                <Link
                  to="/notification-settings"
                  onClick={() => setOpen(false)}
                  className="text-[11px] tracking-wide-luxury text-muted-foreground hover:text-foreground"
                >
                  Settings
                </Link>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                You're all caught up.
              </div>
            ) : (
              <ul>
                {items.map((n) => {
                  const inner = (
                    <div
                      className={`px-4 py-3 border-b border-border hover:bg-muted/40 cursor-pointer ${!n.read_at ? "bg-accent/5" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read_at && (
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                          <p className="text-[10px] text-muted-foreground/70 mt-1 tracking-wide-luxury uppercase">
                            {new Date(n.created_at).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                  return (
                    <li key={n.id} onClick={() => onItemClick(n)}>
                      {n.link ? (
                        <a href={n.link} onClick={() => setOpen(false)}>
                          {inner}
                        </a>
                      ) : (
                        inner
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
